import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { type IEBNotification } from '@shared/models/entities/notification.interface';
import { TranslateService } from '@core/services/translate.service';
import {
  NotificationTarget,
  NotificationTargetResolverService,
} from '@core/services/notification-target-resolver.service';
import { UtilityDockStore } from '../../shell/utility-dock/utility-dock.store';

@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
  private readonly router = inject(Router);
  private readonly message = inject(NzMessageService);
  private readonly utilityDockStore = inject(UtilityDockStore);
  private readonly resolver = inject(NotificationTargetResolverService);
  private readonly translate = inject(TranslateService);

  /**
   * Navigate to the entity referenced by the given notification.
   * Closes the notification panel after a successful navigation.
   * Degrades gracefully when the entity information is missing or unknown.
   */
  navigateToNotification(notification: IEBNotification | undefined | null): void {
    if (!notification) {
      return;
    }

    const target = this.resolver.resolve(notification, { platform: 'desktop' });
    if (target.type === 'unavailable') {
      this.message.info(this.text(target.messageKey));
      return;
    }

    try {
      this.navigateToTarget(target);
    } catch (err) {
      console.error('Notification navigation failed', err);
      this.message.error(this.text('notification.navigation.openFailed'));
    }
  }

  private navigateToTarget(target: NotificationTarget): void {
    this.router
      .navigate(target.commands, target.navigationExtras)
      .then((ok) => {
        if (ok) {
          this.utilityDockStore.closeNotifications();
          return;
        }
        this.message.info(this.text('notification.navigation.unknownTarget'));
      })
      .catch(() => this.message.error(this.text(this.errorKeyFor(target))));
  }

  private errorKeyFor(target: NotificationTarget): string {
    if (target.kind === 'task') {
      return 'notification.navigation.taskOpenFailed';
    }
    if (target.kind === 'thread') {
      return 'notification.navigation.discussionOpenFailed';
    }
    return 'notification.navigation.campaignOpenFailed';
  }

  private text(key: string): string {
    return this.translate.instant(key) || key;
  }
}
